/*global Flybits*/
import { html, css } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import Zone from '../models/zone';
import CtxAPI from '../apis/ctx';
import FlybitsSlot from './flybits-slot';
import ObjUtil from '../utils/obj';

/**
 * @class FlybitsOverlay
 * @memberof Flightkit
 * @extends Flightkit.FlybitsSlot
 */
export default class FlybitsOverlay extends FlybitsSlot {
  static properties = {
    dismissUI: {
      type: Boolean,
      attribute: 'dismiss-ui'
    }
  }

  static styles = css`
    :host {
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1000000;
    }
    .flb-frame {
      width: 100%;
      height: 100%;
      border: 0;
      z-index: 1;
    }
    .dismiss.btn{
      position: fixed;
      top: 25px;
      right: 25px;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      line-height: 40px;
      text-align: center;
      font-size: 25px;
      cursor: pointer;
      background-color: white;
    }
    .dismiss.btn:hover{
      background-color: whitesmoke;
      transition: background-color 0.1s linear; 
    }
    .slot-default flybits-loading{
      background-color: rgba(0,0,0,0.05);
    }
    .slot-default,::slotted(:first-child){
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      visibility: hidden;
      opacity: 0;
      transition: opacity 0.2s linear 0.2s, visibility 0s linear 0.2s;
      z-index: 2;
    }
    .slot-default.open,.open::slotted(:first-child) {
      visibility: visible;
      opacity: 1;
      transition: opacity 0.2s linear 0.2s, visibility 0s linear 0s;
    }
  `

  constructor(){
    super();
    this.renderMode = FlybitsSlot.modes.FULLHEIGHT;
    this.refID = 'overlay';
    this.uid = ObjUtil.guid(2);
    this.dismissUI = false;
  }

  connectedCallback() {
    super.connectedCallback()
    this.setLoading(true);
    const $el = this;
    this.frameLoadPromise.then(function(){
      $el.setLoading(false);
    })

    if(window.Flybits?.evt){
      Flybits.evt.on(Flybits.evt.type.SESSIONEND+'.overlay-'+this.uid, function(){
        $el._dispatchDismiss();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if(window.Flybits?.evt){
      Flybits.evt.off(Flybits.evt.type.SESSIONEND+'.overlay-'+this.uid);
    }
  }
  
  _dispatchDismiss() {
    this.remove();
  }

  _adoptSlotContext(slot){
    if(!slot || !(slot instanceof FlybitsSlot)){
      this.logger.error('invalid argument: can only adopt the context of FlybitsSlot instances');
      return;
    }

    if(slot?.renderURL){
      this.renderURL = slot.renderURL;
    }
    if(slot?.overlayOutlet){
      this.overlayOutlet = slot.overlayOutlet;
    }
    if(slot?.overlayDismissUI){
      this.overlayDismissUI = slot.overlayDismissUI;
    }
    if(slot?.logger?.mode){
      this.logger.mode = slot.logger.mode;
    }
    if(slot?.logLevel){
      this.logLevel = slot.logLevel;
    }
    if(Object.prototype.hasOwnProperty.call(slot, 'dismissUI')){
      this.dismissUI = !!slot.dismissUI;
    }
    if(slot?.$overlayLoadingUI){
      slot.$overlayLoadingUI.setAttribute('slot', 'loading');
      this.append(slot.$overlayLoadingUI);
    }
    if(slot?.$overlayErrorUI){
      slot.$overlayErrorUI.setAttribute('slot', 'error');
      this.append(slot.$overlayErrorUI);
    }
    if(slot?.localization){
      this.localization = slot.localization;
    }
    if(slot?.textDirection){
      this.textDirection = slot.textDirection;
    }
    if(slot?._$evtSrcCtx){
      this._$evtSrcCtx = slot?._$evtSrcCtx;
    }
    if(slot?.overrideURLs){
      this.overrideURLs = slot?.overrideURLs;
    }
  }

  /**
   * 
   * @instance
   * @memberof Flightkit.FlybitsOverlay
   * @function injectContent
   * @param {external:FlybitsContent} content 
   */
  injectContent(content) {
    this.injectData(Zone.generateOverlayPayload(content, this.resolvedLocale));
  }

  detectFlybits() {
    CtxAPI.autoInitCtxManager();
    var $el = this;

    if(window.Flybits){
      if(this.host){
        Flybits.cfg.HOST = this.host;
      }
      if(this.projectID){
        Flybits.Session._projectID = this.projectID;
      }
    } else{
      this.logger.error('Flybits.js Core SDK not detected.');
    }
  }

  render() {
    let $dismissUI = html``;
    if(this.dismissUI){
      $dismissUI = html`
        <div class="dismiss btn" @click=${this._dispatchDismiss}>×</div>
      `;
    }
    return html`
      ${$dismissUI}
      <iframe src="${this.renderURL}" @load=${this._handleFrameLoad} frameborder="0" class="flb-frame" sandbox="allow-scripts allow-same-origin allow-downloads"></iframe>
      ${this.hasLoadingSlot?'':html`
        <div class=${classMap({open: this.isLoadingOpen,'slot-default':true,'loading': true})}>
          <flybits-loading></flybits-loading>
        </div>
      `}
      ${this.hasErrorSlot?'':html`
        <div class=${classMap({open: this.isErrorOpen,'slot-default':true,'error': true})}>
          <flybits-error @reload=${this._handleSlotEvent}></flybits-error>
        </div>
      `}
      <slot class=${classMap({open: this.isLoadingOpen})} name="loading" @slotchange=${this._handleLoadingSlotChange}></slot>
      <slot class=${classMap({open: this.isErrorOpen})} name="error" @reload=${this._handleSlotEvent} @slotchange=${this._handleErrorSlotChange}></slot>
    `
  }
}
customElements.define('flybits-overlay', FlybitsOverlay);